<template>
    <div class="ax-road-page" :class="{ 'ax-road-page--padded': !noPadding }">
        <div v-if="isLoading" class="l-stack l-center l-padded">
            <VSpinner size="medium" :line-fg-color="spinnerColor" :speed="1" />
        </div>

        <div v-else class="ax-road-page__head" :class="headClass">
            <h1 class="l-grow-1">{{ title }}</h1>

            <slot name="header" />

            <IconButton
                v-if="expandable"
                :title="isSubchildExpanded ? $t('unexpand') : $t('expand')"
                @click="handleExpand"
            >
                <ShrinkIcon v-if="isSubchildExpanded" width="14" height="14" />
                <ExpandIcon v-else width="14" height="14" />
            </IconButton>

            <IconButton
                v-if="closable"
                :title="$t('close')"
                @click="handleClose"
            >
                <RemoveIcon />
            </IconButton>
        </div>

        <div class="ax-road-page__body" :class="bodyClass">
            <slot />
        </div>
    </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import VSpinner from 'vue-simple-spinner'

import ExpandIcon from '@/components/icons/ExpandIcon'
import IconButton from '@/components/IconButton'
import RemoveIcon from '@/components/icons/RemoveIcon'
import ShrinkIcon from '@/components/icons/ShrinkIcon'

export default {
    name: 'AxRoadPage',
    components: {
        ExpandIcon,
        IconButton,
        RemoveIcon,
        ShrinkIcon,
        VSpinner,
    },
    props: {
        bodyClass: {
            type: [Array, Object, String],
            default: null,
        },
        closable: {
            type: Boolean,
            default: true,
        },
        expandable: {
            type: Boolean,
            default: false,
        },
        headClass: {
            type: [Array, Object, String],
            default: null,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        noPadding: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
        }
    },
    computed: {
        ...mapState('axroad', ['isSubchildExpanded']),
    },
    methods: {
        ...mapMutations('axroad', ['toggleSubchildExpanded']),
        handleClose() {
            if (this.isSubchildExpanded) {
                this.toggleSubchildExpanded()
            }

            const parentPath = this.$route.path
                .split('/')
                .slice(0, -1)
                .join('/')

            if (parentPath) {
                this.$router.push({
                    path: parentPath,
                    query: this.$route.query,
                })
            } else {
                this.$router.push('/')
            }
        },
        handleExpand() {
            this.toggleSubchildExpanded()
            this.$nextTick(() => {
                this.$emit('expand')
            })
        },
    },
}
</script>

<i18n>
{
  "en": {
    "close": "Close view",
    "expand": "Expand view",
    "unexpand": "Reduce view"
  },
  "de": {
    "close": "Ansicht schließen",
    "expand": "Ansicht erweitern",
    "unexpand": "Ansicht verkleinern"
  },
  "fr": {
    "close": "Fermer la vue",
    "expand": "Agrandir la vue",
    "unexpand": "Réduire la vue"
  },
  "it": {
    "close": "Chiudi vista",
    "expand": "Espandi vista",
    "unexpand": "Riduci vista"
  }
}
</i18n>

<style lang="scss">
.ax-road-page {
    $self: &;
    overflow-y: auto;

    &__head {
        position: sticky;
        top: 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        padding: 24px 24px 12px;
        border-bottom: $style-border;
        background-color: #fff;
        z-index: 1005;

        h1 {
            font-size: 20px;
            line-height: 28px;
            font-weight: 500;
        }
    }

    &__body {
        padding: 0 0 24px;
    }

    &--padded {
        #{$self}__head {
            margin: 0 24px;
            padding-right: 0;
            padding-left: 0;
        }

        #{$self}__body {
            margin: 24px;
            padding: 0;
        }
    }

    @include respond-to('for-mobile-down') {
        margin-left: 58px;
        width: calc(100% - 58px) !important;
    }
}
</style>
